import { Phone, Star, MapPin, Clock, Instagram, Facebook, ArrowRight, Check, Mail } from "lucide-react";

import serviceInterior from "@/assets/service-interior.jpg";
import serviceFacade from "@/assets/service-facade.jpg";
import serviceWaterproof from "@/assets/service-waterproof.jpg";
import serviceRestoration from "@/assets/service-restoration.jpg";
import serviceReform from "@/assets/service-reform.jpg";
import serviceCarpentry from "@/assets/service-carpentry.jpg";

const PHONE_DISPLAY = "627 27 17 94";
const PHONE_HREF = "tel:+34627271794";
const GOOGLE_MAPS_QUERY = "Pinturas+Founes,+Carrer+de+Joan+Munar+11,+07004+Palma";
const GOOGLE_REVIEWS_URL = `https://www.google.com/search?q=Pinturas+Founes+Palma+rese%C3%B1as`;
const INSTAGRAM_URL = "https://www.instagram.com/pinturasfounes/";
const FACEBOOK_URL = "https://www.facebook.com/p/Pinturas-Founes-100064185120858/";

const services = [
  {
    title: "Pintura interior · Acabado Q4",
    desc: "Paredes y techos con acabado liso impecable, sin imperfecciones. La calidad que se ve y se toca.",
    img: serviceInterior,
  },
  {
    title: "Rehabilitación de edificios e ITE",
    desc: "Inspección Técnica de Edificios, fachadas, patios y zonas comunes. Cumplimos la normativa y dejamos el edificio como nuevo.",
    img: serviceFacade,
  },
  {
    title: "Impermeabilizaciones",
    desc: "Cubiertas, terrazas y zonas húmedas con sistemas duraderos frente al sol y la lluvia mediterránea.",
    img: serviceWaterproof,
  },
  {
    title: "Restauraciones",
    desc: "Recuperamos paredes de piedra, molduras y elementos originales de casas mallorquinas con mimo.",
    img: serviceRestoration,
  },
  {
    title: "Reformas integrales",
    desc: "Coordinamos toda la obra: paleta, pintura, carpintería e instalaciones. Tú solo abres la puerta.",
    img: serviceReform,
  },
  {
    title: "Carpintería de madera",
    desc: "Puertas, persianas mallorquinas y armarios: barnizado, restauración y acabados a medida.",
    img: serviceCarpentry,
  },
] as const;

const reasons = [
  { title: "Trato cercano", desc: "Te escuchamos, asesoramos y te tratamos como vecino, no como número." },
  { title: "Profesionalidad", desc: "Equipo con experiencia y acabados de nivel profesional, sin atajos." },
  { title: "Precios competitivos", desc: "Presupuesto claro y sin sorpresas. La mejor calidad al precio justo." },
  { title: "Toda Mallorca", desc: "Nos desplazamos por toda la isla, desde Palma hasta Pollença o Manacor." },
] as const;

const schedule = [
  ["Lunes", "9:00 – 17:00"],
  ["Martes", "9:00 – 17:00"],
  ["Miércoles", "9:00 – 17:00"],
  ["Jueves", "9:00 – 17:00"],
  ["Viernes", "9:00 – 17:00"],
  ["Sábado", "9:00 – 13:00"],
  ["Domingo", "Cerrado"],
] as const;

function Stars({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex gap-0.5 ${className}`} aria-label="5 de 5 estrellas">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-4 w-4 fill-brand-blue text-brand-blue" />
      ))}
    </span>
  );
}

function CallButton({ variant = "primary", className = "" }: { variant?: "primary" | "ghost"; className?: string }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold uppercase tracking-wide transition-all";
  const styles =
    variant === "primary"
      ? "bg-brand-blue text-paper hover:brightness-95 shadow-[0_8px_24px_-8px_rgba(37,99,235,0.5)]"
      : "border border-white/30 text-white hover:bg-white/10";
  return (
    <a href={PHONE_HREF} className={`${base} ${styles} ${className}`} aria-label={`Llamar al ${PHONE_DISPLAY}`}>
      <Phone className="h-4 w-4" />
      <span>{PHONE_DISPLAY}</span>
    </a>
  );
}

export default function App() {
  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-paper/85 backdrop-blur">
        <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3">
          <a href="#top" className="flex min-w-0 items-center gap-3">
            <img
              src="/logo-founes-transparente.png"
              alt="Pinturas Founes"
              className="h-10 w-10 shrink-0"
              width={40}
              height={40}
            />
            <span className="truncate text-sm font-bold uppercase tracking-widest">
              Pinturas Founes
            </span>
          </a>
          <a
            href={PHONE_HREF}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-ink px-4 py-2 text-xs font-semibold text-paper hover:bg-ink/90 sm:text-sm"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            <span className="sm:hidden">Llamar</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative isolate overflow-hidden bg-ink text-paper">
        <img
          src="/hero-painter.jpg"
          alt="Pintor profesional aplicando acabado Q4 en una pared"
          width={1600}
          height={1200}
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
        {/* Brand drip accents */}
        <div className="pointer-events-none absolute -top-10 right-6 -z-10 hidden h-40 w-3 rotate-12 rounded-full bg-brand-blue/80 sm:block" />
        <div className="pointer-events-none absolute -top-16 right-20 -z-10 hidden h-56 w-3 rotate-12 rounded-full bg-brand-red/70 sm:block" />
        <div className="pointer-events-none absolute -top-24 right-36 -z-10 hidden h-72 w-3 rotate-12 rounded-full bg-brand-blue/70 sm:block" />

        <div className="mx-auto max-w-6xl px-4 pb-20 pt-14 sm:pt-20 lg:pb-32 lg:pt-28">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium backdrop-blur transition hover:bg-white/10"
          >
            <Stars />
            <span className="font-semibold">5,0 en Google</span>
            <span className="text-white/70">· Cercanos y profesionales</span>
          </a>

          <h1 className="mt-6 text-4xl font-black leading-[0.95] sm:text-6xl lg:text-7xl">
            Pinturas y revestimientos
            <span className="mt-2 block italic text-brand-blue">en toda Mallorca</span>
          </h1>

          <p className="mt-6 max-w-xl text-base text-white/80 sm:text-lg">
            Acabado Q4, rehabilitación de edificios, ITE, impermeabilizaciones, restauraciones y reformas
            integrales. La mejor calidad a precios competitivos, con el trato cercano que nos define.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <CallButton />
            <a
              href="#servicios"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-white/10"
            >
              Ver servicios <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <dl className="mt-12 grid max-w-md grid-cols-3 gap-4 border-t border-white/15 pt-6 text-xs sm:text-sm">
            <div>
              <dt className="text-white/60">Valoración</dt>
              <dd className="mt-1 font-display text-2xl">5,0★</dd>
            </div>
            <div>
              <dt className="text-white/60">Cobertura</dt>
              <dd className="mt-1 font-display text-2xl">Mallorca</dd>
            </div>
            <div>
              <dt className="text-white/60">Acabado</dt>
              <dd className="mt-1 font-display text-2xl">Q4</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="mx-auto max-w-6xl px-4 py-16 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Servicios</p>
          <h2 className="mt-3 text-3xl font-black leading-tight sm:text-5xl">
            Pinturas y revestimientos <span className="italic">de todo tipo</span>
          </h2>
          <p className="mt-4 text-muted-foreground">
            Interior, exterior, restauración o reforma integral: cubrimos cada fase con los mismos estándares de
            acabado profesional.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <article
              key={s.title}
              className="group overflow-hidden rounded-xl border border-ink/10 bg-card transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  width={1200}
                  height={900}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Por qué nosotros */}
      <section className="bg-ink text-paper">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Por qué Founes</p>
              <h2 className="mt-3 text-3xl font-black leading-tight sm:text-5xl">
                Cercanía y oficio.<br />
                <span className="italic text-brand-blue">Sin atajos.</span>
              </h2>
            </div>
            <p className="text-white/75">
              Llevamos años pintando, rehabilitando y reformando en Mallorca. Lo que nos define no es solo la
              técnica: es escuchar al cliente, cumplir los plazos y dejar cada obra como nos gustaría que la
              dejaran en nuestra propia casa.
            </p>
          </div>

          <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => (
              <li key={r.title} className="rounded-lg border border-white/10 bg-white/5 p-5 backdrop-blur">
                <Check className="h-6 w-6 text-brand-blue" />
                <h3 className="mt-3 text-lg font-bold">{r.title}</h3>
                <p className="mt-1 text-sm text-white/70">{r.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Reseñas Google */}
      <section id="resenas" className="mx-auto max-w-6xl px-4 pt-16 pb-6 sm:pt-24 sm:pb-10">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Lo que dicen nuestros clientes</p>
            <h2 className="mt-3 text-3xl font-black leading-tight sm:text-5xl">
              <span className="italic">5,0</span> de 5 en Google
            </h2>
            <p className="mt-4 text-muted-foreground">
              Pocas reseñas, todas con la máxima puntuación. Una prueba de que cada cliente que confía en
              nosotros se queda satisfecho con el resultado y el trato.
            </p>

            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-ink px-5 py-3 text-sm font-semibold text-paper transition hover:bg-ink/90"
            >
              Leer las reseñas en Google <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="relative overflow-hidden rounded-xl border border-ink/10 bg-gradient-to-br from-brand-blue/15 via-paper to-brand-blue/10 p-8 sm:p-10">
            <div className="flex items-baseline gap-4">
              <span className="font-display text-7xl leading-none sm:text-8xl">5,0</span>
              <div>
                <Stars className="text-base" />
                <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">Google Reviews</p>
              </div>
            </div>

            <blockquote className="mt-8 border-l-2 border-brand-blue pl-5 text-lg italic leading-relaxed">
              “Cercanía, profesionalidad y acabados impecables.”
              <footer className="mt-3 text-sm not-italic text-muted-foreground">
                — El mensaje común de quienes ya han trabajado con nosotros.
              </footer>
            </blockquote>

            <div className="mt-8 flex flex-wrap gap-3">
              {["Cercanía", "Calidad", "Cumplimiento"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-md border border-ink/15 bg-paper px-3 py-2 text-xs font-semibold uppercase tracking-wide"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Contacto */}
      <section id="contacto" className="mx-auto max-w-6xl px-4 pt-6 pb-16 sm:pt-10 sm:pb-24">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Contacto</p>
            <h2 className="mt-3 text-3xl font-black leading-tight sm:text-5xl">
              ¿Tienes un proyecto?<br />
              <span className="italic">Llámanos.</span>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Te visitamos sin compromiso, valoramos el trabajo y te damos un presupuesto claro. Trabajamos en
              toda Mallorca.
            </p>

            <a
              href={PHONE_HREF}
              className="mt-8 flex items-center gap-4 rounded-lg border border-ink/10 bg-paper p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-brand-blue text-paper">
                <Phone className="h-6 w-6" />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-widest text-brand-blue">Llamar ahora</span>
                <span className="block text-2xl font-black tracking-tight text-ink sm:text-3xl">{PHONE_DISPLAY}</span>
              </span>
            </a>

            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-brand-blue" />
                <span>Carrer de Joan Munar, 11, bajos · 07004 Palma, Illes Balears</span>
              </li>
              <li className="flex gap-3">
                <Clock className="h-5 w-5 shrink-0 text-brand-blue" />
                <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1">
                  {schedule.map(([day, hours]) => (
                    <div key={day} className="contents">
                      <span className="font-medium">{day}</span>
                      <span className="text-muted-foreground">{hours}</span>
                    </div>
                  ))}
                </div>
              </li>
              <li className="flex flex-wrap gap-3 pt-2">
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-ink hover:text-paper">
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
                <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-ink hover:text-paper">
                  <Facebook className="h-4 w-4" /> Facebook
                </a>
              </li>
            </ul>
          </div>

          <div className="overflow-hidden rounded-lg border border-ink/10 shadow-lg">
            <iframe
              title="Ubicación de Pinturas Founes en Palma"
              src={`https://www.google.com/maps?q=${GOOGLE_MAPS_QUERY}&output=embed`}
              className="h-[420px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ink/10 bg-ink text-paper">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:grid-cols-[1.2fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo-founes-transparente.png" alt="Pinturas Founes" className="h-12 w-12" width={48} height={48} />
              <div>
                <p className="font-display text-xl">Pinturas Founes</p>
                <p className="text-xs uppercase tracking-widest text-white/60">Aplicaciones y revestimientos</p>
              </div>
            </div>
            <p className="mt-4 max-w-xs text-sm text-white/70">
              Pintura, rehabilitación e impermeabilización profesional en toda Mallorca.
            </p>
          </div>

          <div className="text-sm">
            <p className="font-display text-lg">Contacto</p>
            <ul className="mt-3 space-y-2 text-white/80">
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-white" /> <a href={PHONE_HREF} className="hover:underline">{PHONE_DISPLAY}</a></li>
              <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-white" /> Carrer de Joan Munar 11, Palma</li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-white" /> <a href={`mailto:info@pinturasfounes.com`} className="hover:underline">info@pinturasfounes.com</a></li>
            </ul>
          </div>

          <div className="text-sm">
            <p className="font-display text-lg">Síguenos</p>
            <ul className="mt-3 space-y-2 text-white/80">
              <li><a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:underline"><Instagram className="h-4 w-4" /> @pinturasfounes</a></li>
              <li><a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:underline"><Facebook className="h-4 w-4" /> Pinturas Founes</a></li>
              <li><a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:underline"><Star className="h-4 w-4 fill-white text-white" /> Reseñas en Google</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} Pinturas Founes · Palma de Mallorca
        </div>
      </footer>
    </div>
  );
}
